import { globalLogger } from '../../qaamgo/helper/global-logger';
import { assertIsString } from '../../lib/test-and-assert/assert-base';
import { testIsArray, testIsNonEmptyString, testIsTrueish } from '../../lib/test-and-assert/test-base';
import { assertIsMeData } from './assert';
import { userManager } from './user-manager';

/**
 * @param {String} name
 * @param {String} email
 *
 * @return {string}
 */
function generateDisplayName(name, email) {
    assertIsString(name);

    if (name.trim() === '') {
        return '';
    }

    let _name = name;

    if (testIsNonEmptyString(email)) {
        const reg1 = /^USER_[a-f0-9]{12}$/i;
        const reg2 = /^PDF2GO_[a-f0-9]{12}$/i;

        if (reg1.test(name) || reg2.test(name)) {
            _name = email;
        }
    }

    if (_name.length > 23) {
        _name = _name.slice(0, 20) + '...';
    }

    return _name;
}

class UserData {
    /**
     * @param {MeData} meData
     */
    constructor(meData) {
        assertIsMeData(meData);

        /** @type {MeData} */
        this.meData = meData;

        this.name = '';
        this.email = '';
        this.isRegistered = false;
        this.isPremium = false;
        this.isEducational = false;
        this.isEligibleForFreeTrial = false;
        this.isFromEurope = meData.extra.europe;
        this.country = meData.extra.country;
        this.socialError = null;
        this.sendInvoiceMail = false;
        this.automatedDownloads = true;
        this.isBusinessUser = meData.business_user === true;
        this.constraints_status = null;
        this.enabled = false;

        if (testIsArray(meData.constraints_status)) {
            this.constraints_status = meData.constraints_status;
        }
        if (testIsArray(meData.user_settings)) {
            meData.user_settings.forEach((setting) => {
                if (setting.settings_key === 'sendInvoiceMail') {
                    this.sendInvoiceMail = testIsTrueish(setting.settings_value);
                }

                if (setting.settings_key === 'automatedDownloads') {
                    this.automatedDownloads = testIsTrueish(setting.settings_value);
                }
            });
        }

        if (testIsNonEmptyString(meData.extra.social_error)) {
            this.socialError = meData.extra.social_error;
        }

        if (meData.roles.includes('ROLE_USER')) {
            this.isRegistered = true;
            this.isPremium = false;
            this.name = meData.username;
            if (testIsNonEmptyString(meData.plain_email)) {
                this.email = meData.plain_email;
            }
        }

        if (meData.roles.includes('ROLE_USER_PREMIUM')) {
            this.isRegistered = true;
            this.isPremium = true;
            this.name = meData.username;
            if (testIsNonEmptyString(meData.plain_email)) {
                this.email = meData.plain_email;
            }
        }

        if (meData.roles.includes('ROLE_USER_EDUCATIONAL')) {
            this.isRegistered = true;
            this.isEducational = true;
            this.name = meData.username;
            if (testIsNonEmptyString(meData.plain_email)) {
                this.email = meData.plain_email;
            }
        }

        if (!this.isRegistered || meData.roles.includes('ROLE_USER_ELIGIBLE_FOR_TRIAL')) {
            this.isEligibleForFreeTrial = true;
        }

        if (testIsTrueish(meData.enabled)) {
            this.enabled = true;
        }

        this.displayName = generateDisplayName(this.name, this.email);
    }

    /**
     * @param {string} name
     */
    getConstraintByName(name) {
        return this.constraints_status.find(
            (constraint_status) => constraint_status.ident === name && constraint_status.hit === true
        );
    }
}

const SESSION_STORAGE_USER = '__SESSION_STORAGE_USER__';

/**
 * This returns a UserData object from the session storage. If it is not available
 * or contains wrong data it returns null
 *
 * @return {UserData|null}
 */
function loadUserDataFromSessionStorage() {
    try {
        var encoded = window.sessionStorage.getItem(SESSION_STORAGE_USER);

        /** @type {MeData} */
        const meData = JSON.parse(encoded);

        return new UserData(meData);
    } catch (e) {}

    return null;
}

/**
 * @param {UserData} userData
 */
function saveUserDataToSessionStorage(userData) {
    assertIsMeData(userData.meData);

    try {
        const s = JSON.stringify(userData.meData);

        window.sessionStorage.setItem(SESSION_STORAGE_USER, s);
    } catch (e) {}
}

/**
 * This function is used to get information about the current user from the api
 *
 * The result of the request are cached for the lifetime of the current page to prevent
 * too many requests
 *
 * @return {JQueryDeferred<UserData, String>}
 */
function fetchUserData() {
    var deferred = $.Deferred();

    // fetch /me data via page store - false to make sure that the request is sent only once
    // (there's caching inside the store)
    userManager
        .getUser()
        .then((user) => {
            const me = user.getRawMeData();

            /** @type {?UserData} */
            let userData = null;

            try {
                userData = new UserData(me);
            } catch (e) {
                globalLogger.log('sat user exception', e);
            }

            if (userData === null) {
                globalLogger.log('get user data - fail - invalid data', JSON.stringify(me));

                deferred.reject('invalid');

                return;
            }

            deferred.resolve(userData);
        })
        .catch((e) => {
            globalLogger.log('get user data - fail - connection problem', {});

            deferred.reject('connection problem');
        });

    return deferred;
}

class SatUserManager {
    constructor() {
        /**
         * This stores all deferred which arrive while the ajax request is ongoing
         *
         * @type {JQueryDeferred[]}
         */
        this.callbacks = [];

        /**
         * This stores the user data after a successful request
         *
         * @type {null|UserData}
         */
        this.userData = null;

        this.isLoading = false;
    }

    _resolveAllCallbacks(data) {
        for (var i = 0; i < this.callbacks.length; i++) {
            this.callbacks[i].resolve(data);
        }

        this.callbacks = [];
    }

    _rejectAllCallbacks(error) {
        for (var i = 0; i < this.callbacks.length; i++) {
            this.callbacks[i].reject(error);
        }

        this.callbacks = [];
    }

    getUser() {}

    /**
     * @deprecated
     * This function is used to get information about the current user from the api
     *
     * The result of the request are cached for the lifetime of the current page to prevent
     * too many requests
     *
     * @return {JQueryDeferred<UserData>}
     */
    getUserDataDeprecated() {
        var deferred = $.Deferred();

        // when we already have user data we just return the data
        if (this.userData !== null) {
            deferred.resolve(this.userData);

            return deferred;
        }

        // if there is already an acitve ajax request we store the callback for later.
        // they will be resolved/rejected when ajax request is done
        if (this.isLoading) {
            this.callbacks.push(deferred);

            return deferred;
        }

        this.isLoading = true;

        fetchUserData()
            .done((userData) => {
                this.isLoading = false;

                saveUserDataToSessionStorage(userData);

                this.userData = userData;

                this._resolveAllCallbacks(userData);

                deferred.resolve(userData);
            })
            .fail((error) => {
                this.isLoading = false;

                this.userData = null;

                this._rejectAllCallbacks(error);

                deferred.reject(error);
            });

        return deferred;
    }

    /**
     * This function is used to get information about the current user from cache or the api
     * @deprecated
     * @return {JQueryDeferred<UserData>}
     */
    getUserDataCached() {
        var deferred = $.Deferred();

        // when we already have user data we just return the data
        if (this.getUserDataFromSessionStorage() !== null) {
            deferred.resolve(this.getUserDataFromSessionStorage());

            return deferred;
        }

        // when we already have user data we just return the data
        if (this.getUserDataFromSessionStorage() !== null) {
            deferred.resolve(this.userData);

            return deferred;
        }

        return this.getUserDataDeprecated();
    }

    /**
     * This returns a UserData object from the session storage. If it is not available
     * or contains wrong data it returns null
     * @deprecated
     * @return {UserData|null}
     */
    getUserDataFromSessionStorage() {
        return loadUserDataFromSessionStorage();
    }
}

if (!global.gSatUserManager) {
    global.gSatUserManager = new SatUserManager();
}

/** @type {SatUserManager} */
let satUserManager = global.gSatUserManager;

export { satUserManager };
